import React, { ChangeEvent, useEffect, useRef, useState } from 'react';

import { Annex, Footer, Form, Legend, List, Wrapper } from './styles';

import CAM from '../../assets/CAM.png';
import Close from '../../assets/Close.png';
import Picture from '../../assets/Picture.png';
import FolderOpen from '../../assets/FolderOpen.png';
import AdobeReader from '../../assets/AdobeReader.png';
import SuccessBlue from '../../assets/SuccessBlue.png';
import { handleCam, handleDocument, handlePhoto, sha256 } from './functions';
import { uploadAPI } from './services';
import { useModuleContext } from '../../contexts/ModuleContext';
import { usePatientContext } from '../../contexts/PatientContext';
import { InputFile } from '../../components/Inputs/File';
import { HeaderUpload } from '../../components/HeaderUpload';
import { Button, LoadingOverlay } from '../../components';
import useOnCloseSchedule from '../../hooks/useOnCloseSchedule';

const Upload = () => {
  const [data, setData] = useState<any>([]);
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [loading, setLoading] = useState(false);
  const [uploadsCompleted, setUploadsCompleted] = useState(false);
  const [enableFinishUpload, setEnableFinishUpload] = useState(false);
  const [disabledInput, setDisabledInput] = useState({
    camInput: false,
    photoInput: false,
    documentInput: false
  });
  const { sdkToken } = useModuleContext();
  const { patientSelected } = usePatientContext();
  const { onCloseSchedule } = useOnCloseSchedule({});

  const uploadAPS = async () => {
    console.log(disabledInput);
    setLoading(true);

    const uploadData = data.map((document: any, index: number) => {
      const blobUrl = URL.createObjectURL(document);
      const documentFile = document.key.keys[0];
      return {
        birthDate: patientSelected?.birthDate,
        contentType: document.type,
        cockpitId: 2,
        multipartHash: blobUrl,
        originalFilename: document.name,
        patientDocument: patientSelected?.document,
        uploadedFilename: documentFile
      };
    });

    try {
      await uploadAPI
        .post('/upload-cockpit/senddata/documents', uploadData, {
          headers: {
            Authorization: `Bearer ${sdkToken}`
          }
        })
        .then(() => {
          setUploadsCompleted(false);
          setLoading(false);
        });
    } catch (e) {
      setUploadsCompleted(false);

      console.error(e);
    }
  };

  const postFileForTransform = async (file: any) => {
    try {
      setLoading(true);

      const data = new FormData();
      data.append('files', file);
      const response = await uploadAPI.post(`/upload/multiple/documents`, data, {
        headers: {
          Authorization: `Bearer ${sdkToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setEnableFinishUpload(!enableFinishUpload);
      return response.data;
    } catch (e) {
      alert('Falha ao fazer upload no s3');
    }
  };

  const handleUploadSignedUrl = async () => {
    setLoading(true);

    const uploadPromises = data.map((item: any) => {
      if (!item.uploaded) {
        return postFileForTransform(item).then(key => {
          item.key = key;
          item.uploaded = true;
        });
      }
      return Promise.resolve();
    });

    await Promise.all(uploadPromises);
    setUploadsCompleted(true);
  };

  const uploadDocuments = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      handleUploadSignedUrl();
    } catch (error) {
      console.log('error', error);
      alert('Falha ao fazer upload');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const performUploads = async () => {
      await uploadAPS();
      onCloseSchedule();
    };

    if (uploadsCompleted === true) {
      performUploads();
    }
  }, [uploadsCompleted]);

  const handleChange = async (event: ChangeEvent<HTMLInputElement>, id: string) => {
    const files: any = event.target.files;
    if (!files) return;
    const newFilesCount = files.length;

    if (data.length >= 3) {
      alert('Você só pode adicionar até 3 itens.');
      return;
    }

    for (let i = 0; i < newFilesCount; i++) {
      const file = files[i];
      try {
        switch (id) {
          case 'photo':
            try {
              const photoModel = await handlePhoto(file);
              setData((prev: any) => [...prev, photoModel]);
              setDisabledInput(prevInputs => ({
                ...prevInputs,
                photoInput: true
              }));
            } catch (e) {
              console.error(e);
            }
            break;
          case 'document':
            if (file.type === 'application/pdf') {
              try {
                const documentModel = await handleDocument(file);
                setData((prev: any) => [...prev, documentModel]);
                setDisabledInput(prevInputs => ({
                  ...prevInputs,
                  documentInput: true
                }));
              } catch (e) {
                console.error(e);
              }
            }
            break;
          case 'cam':
            if (file.type.startsWith('image/')) {
              try {
                const camModel = await handleCam(file);
                setData((prev: any) => [...prev, camModel]);
                setDisabledInput(prevInputs => ({
                  ...prevInputs,
                  camInput: true
                }));
              } catch (e) {
                console.error(e);
              }
            }
            break;
          default:
            console.error('Invalid ID');
        }
      } catch (error) {
        console.error('Error handling file:', error);
      }
    }
  };

  const handleClear = (id: string, type: string) => {
    setData((prev: any) => prev.filter((fileInfo: any) => fileInfo.idToDelete !== id));
    const fileResponse = inputRefs.current[id];
    if (fileResponse) {
      fileResponse.value = '';
    }

    if (type === 'documentInput' && inputRefs.current['document']) {
      inputRefs.current['document'].value = '';
    } else if (type === 'photoInput' && inputRefs.current['photo']) {
      inputRefs.current['photo'].value = '';
    } else if (type === 'camInput' && inputRefs.current['cam']) {
      inputRefs.current['cam'].value = '';
    }

    setDisabledInput(prevInputs => ({
      ...prevInputs,
      [type]: false
    }));
  };

  useEffect(() => {
    if (data.length === 0 && inputRefs.current['photo'] && inputRefs.current['cam'] && inputRefs.current['document']) {
      inputRefs.current['photo'].value = '';
      inputRefs.current['cam'].value = '';
      inputRefs.current['document'].value = '';
    }
  }, [data]);

  return (
    <>
      <HeaderUpload title="Anexar documentos" hasGoBackButton />

      <Form onSubmit={uploadDocuments}>
        <fieldset>
          <Legend>
            <img src={FolderOpen} alt="Ícone de pasta de documentos" />

            <span>
              <p>Selecione até 3 arquivos.</p>
              <p>
                Armazenamento máximo: <b>15MB</b>
              </p>
            </span>
          </Legend>

          <Wrapper>
            <InputFile
              id="document"
              icon={AdobeReader}
              title="Documentos ou exames"
              accept="application/pdf"
              ref={el => (inputRefs.current['document'] = el)}
              onChange={e => handleChange(e, 'document')}
              multiple
              disabled={data.length >= 3}
              data-testid="file-input"
            />
            <InputFile
              id="photo"
              icon={Picture}
              title="Fotos"
              accept="image/*"
              ref={(el: any) => (inputRefs.current['photo'] = el)}
              onChange={e => handleChange(e, 'photo')}
              disabled={data.length >= 3}
              data-testid="file-input"
              multiple
            />

            {/* <InputFile
              id="cam"
              icon={CAM}
              title="Câmera"
              accept="image/*"
              multiple
              capture="environment"
              ref={el => (inputRefs.current['cam'] = el)}
              onChange={e => handleChange(e, 'cam')}
              disabled={data.length >= 3}
              data-testid="file-input"
              /> */}
          </Wrapper>

          <List>
            <li>
              <h3>Anexados</h3>
            </li>
            {data &&
              data.map((data: any, index: number) => (
                <li key={index}>
                  <Annex>
                    <span>
                      <img src={SuccessBlue} alt="Ícone checado" />
                      <p>{data.name}</p>
                    </span>

                    <span>
                      <p>{(data.size / (1024 * 1024)).toFixed(2)}MB</p>

                      <button type="button" onClick={() => handleClear(data.idToDelete, data.typeInput)}>
                        <img src={Close} alt="Ícone fechar" />
                      </button>
                    </span>
                  </Annex>
                </li>
              ))}
          </List>
        </fieldset>
        <Footer>
          <Button
            disabled={data.length === 0}
            title={`${data.length === 0 ? 'Enviar' : 'Concluir'}`}
            type="submit"
            onClick={() => {}}
          />
        </Footer>
        <LoadingOverlay loading={loading} />
      </Form>
    </>
  );
};

export { Upload };
